import { BundleItem, InAppPurchaseWebPrice } from 'mytours-read-api-typescript'
import { BundleProducts, CollectionWebPrices } from 'mytours-read-common'

export type Money = { currency: string; cents: number }

export type BundleOfferSummary = {
	/** What the bundle sells for, when it is on sale at all; a redeem-only bundle has none. */
	price?: Money
	/** Undefined unless every item is a collection sold on its own in the bundle's currency: measured
	 * against a partial sum, the saving would be understated or negative. */
	saving?: Money
	itemCount: number
	allTours: boolean
}

const toMoney = (webPrice?: InAppPurchaseWebPrice): Money | undefined =>
	webPrice?.amount && webPrice.currency
		? { currency: webPrice.currency, cents: webPrice.cents ?? Math.round(webPrice.amount * 100) }
		: undefined

const savingOf = (price: Money, items: BundleItem[], collectionWebPrices: CollectionWebPrices): Money | undefined => {
	if (items.length === 0) {
		return undefined
	}

	let separately = 0
	for (const item of items) {
		const itemPrice =
			item.item_type === BundleItem.ItemTypeEnum.Collection && item.item_id !== undefined
				? toMoney(collectionWebPrices[item.item_id])
				: undefined
		if (!itemPrice || itemPrice.currency !== price.currency) {
			return undefined
		}
		separately += itemPrice.cents
	}

	return separately > price.cents ? { currency: price.currency, cents: separately - price.cents } : undefined
}

export const summariseBundleOffer = (
	bundleId: number,
	bundleProducts: BundleProducts,
	collectionWebPrices: CollectionWebPrices = {},
): BundleOfferSummary => {
	const product = bundleProducts[bundleId]
	const price = product?.active ? toMoney(product.webPrice) : undefined
	const items = product?.items ?? []

	return {
		price,
		saving: price && savingOf(price, items, collectionWebPrices),
		itemCount: items.length,
		allTours: items.length > 0 && items.every(item => item.item_subtype === BundleItem.ItemSubtypeEnum.CollectionTour),
	}
}

/** The cheapest and the biggest saving across several bundles — only when they share a currency, since
 * "from" and "up to" mean nothing across two. */
export const summariseBundleOffers = (
	summaries: BundleOfferSummary[],
): { lowestPrice?: Money; biggestSaving?: Money } => {
	const prices = summaries.flatMap(summary => (summary.price ? [summary.price] : []))
	const currencies = new Set(prices.map(price => price.currency))
	if (prices.length === 0 || currencies.size > 1) {
		return {}
	}

	const savings = summaries.flatMap(summary => (summary.saving ? [summary.saving] : []))
	const byCents = (a: Money, b: Money) => a.cents - b.cents

	return {
		lowestPrice: [...prices].sort(byCents)[0],
		biggestSaving: savings.length > 0 ? [...savings].sort(byCents)[savings.length - 1] : undefined,
	}
}
